// hooks/usePermission.jsx
// Common hook สำหรับตรวจสอบสิทธิ์ - isSuperuser จะผ่านทุก permission อัตโนมัติ
// VIEW: ถ้ามีสิทธิ์อื่นใน resource เดียวกัน (ADD/CHANGE/DELETE/EXPORT/ฯลฯ) จะถือว่ามองเห็น (VIEW) ได้

import { jwtDecode } from "jwt-decode";
import AuthGroup from "../../configs/authgroup";
import permissionsConfig from "../../configs/permission";

const getUserPermissions = () => {
  const token = localStorage.getItem("token");
  if (!token) return { permissions: [], isSuperuser: false, groups: [] };

  try {
    const decoded = jwtDecode(token);
    return {
      permissions: decoded?.permission || [],
      isSuperuser: decoded?.is_superuser || false,
      groups: decoded?.group_id || [],
    };
  } catch {
    return { permissions: [], isSuperuser: false, groups: [] };
  }
};

/**
 * เช็คสิทธิ์เดียว: ถ้า required เป็น VIEW ของ resource ใดๆ และ user มีสิทธิ์อื่นใน resource นั้น
 * (ไม่ใช่ VIEW) จะถือว่าผ่านการมองเห็น (VIEW) ได้
 */
const userHasPermission = (userPermissions, requiredPermId) => {
  if (requiredPermId == null) return false;
  if (userPermissions.includes(requiredPermId)) return true;

  for (const model of Object.values(permissionsConfig)) {
    if (!model || typeof model !== "object" || model.VIEW !== requiredPermId) continue;
    for (const [key, id] of Object.entries(model)) {
      if (key === "VIEW" || id == null) continue;
      if (userPermissions.includes(id)) return true;
    }
  }
  return false;
};

/**
 * Common permission hook
 * - hasPermission(perm): ใช้เช็คสิทธิ์ (isSuperuser = true จะได้ true เสมอ)
 * - hasGroup(group): ใช้เช็ค group (isSuperuser ผ่านเสมอ)
 */
export const usePermission = () => {
  const { permissions, isSuperuser, groups } = getUserPermissions();

  const hasPermission = (required) => {
    if (isSuperuser) return true;
    if (Array.isArray(required)) {
      return required.some((perm) => userHasPermission(permissions, perm));
    }
    return userHasPermission(permissions, required);
  };

  const hasGroup = (required) => {
    if (isSuperuser) return true;
    return groups.includes(required);
  };

  /** โมดูล company: กลุ่ม DestinationWarehouse ใช้งานได้เทียบเท่า superadmin */
  const isCompanyFullAccess = isSuperuser || groups.includes(AuthGroup.DestinationWarehouse);

  /** โมดูล customer: กลุ่ม DestinationWarehouse ใช้งานได้เทียบเท่า superadmin */
  const isCustomerFullAccess = isSuperuser || groups.includes(AuthGroup.DestinationWarehouse);

  return {
    hasPermission,
    isSuperuser,
    permissions,
    groups,
    hasGroup,
    isCompanyFullAccess,
    isCustomerFullAccess,
  };
};

export const useUser = () => {
  const token = localStorage.getItem("token");
  if (!token) return { companyId: null, saleId: null, customerId: null, customerCode: null };

  try {
    const decoded = jwtDecode(token);
    return {
      companyId: decoded?.company_id ?? null,
      saleId: decoded?.sale_id ?? null,
      customerId: decoded?.customer_id ?? null,
      customerCode: decoded?.customer_code ?? null,
    };
  } catch {
    return { companyId: null, saleId: null, customerId: null, customerCode: null };
  }
};
